<template>
    <div>
        <LoadingSpinner v-if="loader" />
        <!-- Page Content -->
        <div class="page-content">
            <!-- Plans Step -->
            <div v-if="currentStep === 'plans'" class="plans-step">
                <!-- Featured Plan Card -->
                <div class="featured-plan-card">
                    <div class="featured-badge">
                        <span class="mdi mdi-trophy"></span>
                    </div>
                    <div class="featured-content">
                        <h5>{{ $t('boost.visibleTopResults') }}</h5>
                    </div>
                </div>

                <!-- Plan Groups -->
                <div v-for="(groupPlans, groupType) in groupedPlans" :key="groupType" class="plan-group">
                    <div class="plan-group-title">{{ $t(`boosterPlans.type.${groupType}`) }}</div>

                    <div class="boost-plans-list">
                        <div v-for="plan in groupPlans" :key="plan.id" class="boost-plan-option"
                            :class="{ 'selected': selectedPlanIds[groupType] === plan.id }" role="button"
                            :aria-label="`${plan.name} - ${formatPrice(plan.price)}`"
                            :aria-pressed="selectedPlanIds[groupType] === plan.id" tabindex="0"
                            @click="selectPlan(groupType, plan.id)"
                            @keypress.enter.space.prevent="selectPlan(groupType, plan.id)">
                            <div class="plan-radio">
                                <div class="radio-outer">
                                    <div v-if="selectedPlanIds[groupType] === plan.id" class="radio-inner"></div>
                                </div>
                            </div>
                            <div class="plan-info">
                                <div class="plan-duration">{{ plan.name }}</div>
                            </div>
                            <div class="plan-price">{{ formatPrice(plan.price) }}</div>
                        </div>
                    </div>

                    <!-- Description for selected plan in this group -->
                    <transition name="fade">
                        <div v-if="selectedPlanIds[groupType]" class="plan-description-wrapper">
                            <div class="plan-description" v-if="groupPlans.find(p => p.id === selectedPlanIds[groupType])">
                                {{ groupPlans.find(p => p.id === selectedPlanIds[groupType]).description }}
                            </div>
                        </div>
                    </transition>
                </div>

                <div v-if="plans.length === 0" class="empty-state">
                    <span class="mdi mdi-package-variant"></span>
                    <p>{{ $t('sentence.noPlans') }}</p>
                </div>

                <!-- Total and Action Button -->
                <div class="boost-footer">
                    <div class="boost-total">
                        <span class="total-price">{{ formatPrice(totalPrice) }}</span>
                    </div>
                    <div class="d-flex gap-2">

                        <button v-if="$route.name === 'BoostPlanManagementAfterCreate'" class="btn btn-danger"
                            @click="proceedToDone">
                            {{ $t('menu.skip') }}
                        </button>

                        <button class="btn btn-danger" :disabled="!hasSelectedPlans || loadingPaymentMethods"
                            @click="proceedToPayment">
                            <span v-if="loadingPaymentMethods" class="spinner-border spinner-border-sm mr-2"></span>
                            {{ loadingPaymentMethods ? $t('pagination.loading') : $t('menu.next') }}
                        </button>
                    </div>
                </div>
            </div>

            <!-- Payment Step -->
            <div v-if="currentStep === 'payment'" class="payment-step">
                <!-- Loading State -->
                <div v-if="loadingPaymentMethods" class="loading-state">
                    <div class="spinner-border text-primary" role="status">
                        <span class="sr-only">{{ $t('pagination.loading') }}...</span>
                    </div>
                </div>

                <!-- Payment Methods List -->
                <div v-else class="payment-methods-list">
                    <div v-for="method in paymentTypes" :key="method.id" class="payment-method-option"
                        :class="{ 'selected': selectedPaymentType === method.id }" role="button"
                        :aria-label="method.name" :aria-pressed="selectedPaymentType === method.id" tabindex="0"
                        @click="selectPaymentMethod(method.id)"
                        @keypress.enter.space.prevent="selectPaymentMethod(method.id)">
                        <div class="payment-radio">
                            <div class="radio-outer">
                                <div v-if="selectedPaymentType === method.id" class="radio-inner"></div>
                            </div>
                        </div>
                        <div class="payment-info">
                            <div class="payment-name">{{ method.name }}</div>
                            <div v-if="method.description" class="payment-description">
                                {{ method.description }}
                            </div>
                        </div>
                    </div>

                    <div v-if="paymentTypes.length === 0" class="empty-state">
                        <span class="mdi mdi-credit-card-off"></span>
                        <p>{{ $t('payment.noMethods') }}</p>
                    </div>
                </div>

                <!-- Summary and Confirm -->
                <div v-if="!loadingPaymentMethods" class="payment-summary">
                    <div class="summary-row">
                        <span class="summary-label">{{ $t('boost.selectedPlans') }}:</span>
                        <span class="summary-value">{{ selectedPlanCount }}</span>
                    </div>
                </div>

                <div v-if="!loadingPaymentMethods" class="boost-footer">
                    <div class="boost-total">
                        <span class="total-price">{{ formatPrice(totalPrice) }}</span>
                    </div>
                    <button class="btn btn-danger" :disabled="!selectedPaymentType || processingBoost"
                        @click="handleBoost">
                        <span v-if="processingBoost" class="spinner-border spinner-border-sm mr-2"></span>
                        {{ processingBoost ? $t('attr.processing') : $t('action.validateAndPay') }}
                    </button>
                </div>
            </div>

            <!-- Success Step -->
            <div v-if="currentStep === 'created_successfully'" class="success-step">
                <div class="success-content">
                    <div class="success-icon">
                        <span class="mdi mdi-check-circle"></span>
                    </div>
                    <h3 class="success-title">{{ $t('boost.successTitle') }}</h3>
                    <p class="success-message">{{ $t('boost.successMessage') }}</p>

                    <div class="success-details">
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('boost.boostedPlans') }}:</span>
                            <span class="detail-value">{{ selectedPlanCount }}</span>
                        </div>
                        <div class="detail-row">
                            <span class="detail-label">{{ $t('boost.totalPaid') }}:</span>
                            <span class="detail-value">{{ formatPrice(totalPrice) }}</span>
                        </div>
                    </div>

                    <div class="success-actions">
                        <button class="btn btn-primary" @click="goToProduct">
                            {{ $t('boost.viewProduct') }}
                        </button>
                        <button class="btn btn-outline-secondary" @click="resetAndClose">
                            {{ $t('action.close') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import helper from "@/services/helper";
import AlertService from '@/services/alertService';
import LoadingSpinner from "@/components/templates/LoadingSpinner.vue";

export default {
    name: 'BoostPlansPage',
    components: {
        LoadingSpinner
    },
    props: {
        productId: {
            type: [String, Number],
            default: null
        },
        product: {
            type: Object,
            default: null
        },
    },
    data() {
        return {
            currentStep: 'plans',
            selectedPlanIds: {},
            paymentTypes: [],
            selectedPaymentType: null,
            loadingPaymentMethods: false,
            processingBoost: false,
            loader: false,
            productInternal: null,
            plansInternal: [],
            loadingProduct: false
        };
    },
    computed: {
        productData() {
            return this.$props.product||this.productInternal;
        },
        plans() {
            return (Array.isArray(this.$props.plans)&&this.$props.plans.length>0)
                ? this.$props.plans
                :this.plansInternal;
        },
        groupedPlans() {
            const groups={};
            this.plans.forEach(plan => {
                const type=plan.type||'default';
                if (!groups[type]) {
                    groups[type]=[];
                }
                groups[type].push(plan);
            });
            return groups;
        },
        totalPrice() {
            return Object.values(this.selectedPlanIds).reduce((total, planId) => {
                const plan=this.plans.find(p => p.id===planId);
                if (plan&&plan.price) {
                    return total+(parseInt(plan.price)||0);
                }
                return total;
            }, 0);
        },
        hasSelectedPlans() {
            return Object.keys(this.selectedPlanIds).length>0;
        },
        selectedPlanCount() {
            return Object.keys(this.selectedPlanIds).length;
        }
    },
    async mounted() {
        const prodId=this.productId||this.$route.params.id;
        if (!this.$props.product) {
            if (prodId) await this.fetchProduct(prodId);
        }
        await this.loadPlans();
    },
    methods: {
        formatPrice: helper.formatPrice,

        goBack() {
            this.$router.go(-1);
        },

        selectPlan(groupType, planId) {
            if (this.selectedPlanIds[groupType]===planId) {
                this.$delete(this.selectedPlanIds, groupType);
            } else {
                this.$set(this.selectedPlanIds, groupType, planId);
            }
        },

        async proceedToPayment() {
            if (!this.hasSelectedPlans||this.loadingPaymentMethods) return;
            this.loadingPaymentMethods=true;

            try {
                await this.getPaymentTypes();
                if (this.paymentTypes.length>0) {
                    this.currentStep='payment';
                }
            } catch (error) {
                console.error('Failed to load payment methods:', error);
            } finally {
                this.loadingPaymentMethods=false;
            }
        },

        proceedToDone() {
        const prodId=this.productId||this.$route.params.id;
            
            this.$router.push({ name: "ProductCreatedSuccess", params: { id:prodId} });
        },
        backToPlans() {
            this.currentStep='plans';
            this.selectedPaymentType=null;
        },

        async getPaymentTypes() {
            try {
                this.loader=true;
                const response=await this.$axios.get("payment-methods");
                this.paymentTypes=Object.values(response.data.data||{});

                if (this.paymentTypes.length===0) {
                    this.$notify({
                        group: "warning",
                        title: this.$t('warning.title'),
                        type: "warning",
                        text: this.$t('payment.noMethodsAvailable')
                    });
                }
                this.loader=false;
            } catch (err) {
                console.error("Error loading payment methods:", err);
                this.$notify({
                    group: "error",
                    title: this.$t('error.title'),
                    type: "error",
                    text: this.$t('payment.loadError')
                });
                this.loader=false;
                throw err;
            }
        },

        selectPaymentMethod(methodId) {
            this.selectedPaymentType=methodId;
        },

        async fetchProduct(productId) {
            try {
                this.loadingProduct=true;
                const id=productId||this.productId||this.$route.params.id;
                if (!id) return;
                const { data }=await this.$axios.get(`product/boost/${id}`);
                this.productInternal=data;
            } catch (err) {
                console.error('Error fetching product in BoostPlansMenu:', err);
            } finally {
                this.loadingProduct=false;
            }
        },

        async loadPlans() {
            try {
                this.loader=true;
                const res=await this.$axios.get('/get-booster-plans');
                this.plansInternal=Array.isArray(res.data.data)
                    ? res.data.data
                    :Object.values(res.data.data||{});
                this.loader=false;
            } catch (err) {
                console.error('Error loading booster plans in BoostPlansMenu:', err);
                this.loader=false;
            }
        },

        async handleBoostSubmission({ planIds, paymentType }) {
            if (!Array.isArray(planIds)||!planIds.length) return;

            const plans=this.plans.filter(p => planIds.includes(p.id));
            if (!plans.length) return;

            const planNames=plans.map(p => p.name).join(', ');

            try {
                const confirmed=await AlertService.confirm({
                    title: this.$t('boost.confirmTitle'),
                    text: `${this.$t('boost.confirmText')} : ${planNames} ?`
                });

                if (!confirmed) return;

                const productIdToUse=this.productData?.id||this.productId||this.$route.params.id;
                const response=await this.$axios.post('/booster/purchase', {
                    product_id: productIdToUse,
                    plan_ids: planIds,
                    payment_method_id: paymentType
                });

                if (response.data.success) {
                    await this.fetchProduct();
                    this.currentStep='created_successfully';
                }
            } catch (error) {
                console.error('Error boosting product in BoostPlansMenu:', error);
                AlertService.error(
                    error.response?.data?.message||this.$t('boost.error')||'Erreur lors du boost'
                );
            }
        },

        async handleBoost() {
            if (!this.hasSelectedPlans||!this.selectedPaymentType||this.processingBoost) return;

            const payload={
                planIds: Object.values(this.selectedPlanIds),
                paymentType: this.selectedPaymentType
            };

            this.processingBoost=true;
            try {
                await this.handleBoostSubmission(payload);
            } finally {
                this.processingBoost=false;
            }
        },

        goToProduct() {
            const productIdToUse=this.productData?.id||this.productId||this.$route.params.id;
            if (productIdToUse) {

                this.$router.push({ name: "ProductManagement", params: { id: productIdToUse } });
            } else {
                this.goBack();
            }
        },

        resetAndClose() {
            this.currentStep='plans';
            this.selectedPlanIds={};
            this.selectedPaymentType=null;
            this.goBack();
        }
    }
};
</script>
<style scoped>
/* Page Base Styles */
.page-content {
    margin: 0 auto;
    padding: 2rem 0rem;
}

/* Plans Step Styles */
.plans-step,
.payment-step,
.success-step {
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.featured-plan-card {
    background: linear-gradient(135deg, var(--white, #fff) 0%, var(--gray-50, #f9fafb) 100%);
    border: 2px solid var(--gray-100, #f3f4f6);
    border-radius: 16px;
    padding: 24px;
    margin-bottom: 32px;
    position: relative;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.featured-badge {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, var(--color-oo, #d97706) 0%, var(--color-oo-400, #b45309) 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3);
}

.featured-badge .mdi {
    color: var(--white, #fff);
    font-size: 24px;
}

.featured-content h5 {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--color-2, #333);
    margin: 0;
    padding-right: 60px;
    line-height: 1.4;
}

.plan-group {
    margin-bottom: 32px;
    background: var(--white, #fff);
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.plan-group-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 2px solid #e0e0e0;
    text-transform: capitalize;
}

.boost-plans-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.boost-plan-option {
    background: var(--white, #fff);
    border: 2px solid var(--gray-200, #e5e7eb);
    border-radius: 12px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.boost-plan-option:hover {
    border-color: var(--color-oo, #d97706);
    background: var(--danger-light, #fff5f5);
    transform: translateX(4px);
}

.boost-plan-option.selected {
    border-color: var(--color-oo, #d97706);
    background: var(--danger-light, #fff5f5);
    box-shadow: 0 2px 8px rgba(217, 119, 6, 0.15);
}

.plan-radio {
    flex-shrink: 0;
}

.radio-outer {
    width: 22px;
    height: 22px;
    border: 2px solid var(--gray-300, #d1d5db);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.boost-plan-option.selected .radio-outer,
.payment-method-option.selected .radio-outer {
    border-color: var(--color-oo, #d97706);
    border-width: 2px;
}

.radio-inner {
    width: 12px;
    height: 12px;
    background: var(--color-oo, #d97706);
    border-radius: 50%;
}

.plan-info {
    flex: 1;
}

.plan-duration {
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-2, #333);
}

.plan-price {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--color-2, #333);
}

.plan-description-wrapper {
    margin-top: 12px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
}

.plan-description {
    font-size: 0.875rem;
    color: #666;
    line-height: 1.5;
}

.boost-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 32px;
    padding: 24px;
    background: var(--gray-100);
    border-radius: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    gap: 16px;
    width: 100%;
}

.boost-total {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.total-label {
    font-size: 0.875rem;
    color: #666;
}

.total-price {
    font-size: 1.75rem;
    font-weight: bold;
    color: var(--color-oo, #d97706);
}

/* Payment Step Styles */
.loading-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 4rem;
}

.payment-methods-list {
    margin-bottom: 2rem;
}

.payment-method-option {
    display: flex;
    align-items: flex-start;
    padding: 1.25rem;
    margin-bottom: 1rem;
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
    background: var(--white, #fff);
}

.payment-method-option:hover {
    border-color: var(--color-oo, #d97706);
    background-color: var(--danger-light, #fff5f5);
    transform: translateX(4px);
}

.payment-method-option.selected {
    border-color: var(--color-oo, #d97706);
    background-color: var(--danger-light, #fff5f5);
    box-shadow: 0 2px 8px rgba(217, 119, 6, 0.15);
}

.payment-radio {
    margin-right: 1rem;
    padding-top: 0.25rem;
    flex-shrink: 0;
}

.payment-info {
    flex: 1;
}

.payment-name {
    font-weight: 600;
    color: #333;
    margin-bottom: 0.25rem;
    font-size: 1.05rem;
}

.payment-description {
    font-size: 0.875rem;
    color: #666;
    line-height: 1.4;
}

.payment-summary {
    padding: 1.5rem;
    margin: 1.5rem 0;
    background: var(--white, #fff);
    border-radius: 12px;
    border: 2px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.summary-row {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0;
}

.summary-label {
    color: #666;
    font-size: 1rem;
}

.summary-value {
    font-weight: 600;
    color: #333;
    font-size: 1rem;
}

.total-row {
    border-top: 2px solid #dee2e6;
    margin-top: 0.5rem;
    padding-top: 1rem;
    font-size: 1.2rem;
}

.total-row .summary-value {
    color: var(--color-oo, #d97706);
    font-size: 1.4rem;
}

/* Success Step Styles */
.success-step {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    padding: 2rem;
}

.success-content {
    text-align: center;
    max-width: 500px;
    width: 100%;
}

.success-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 24px;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 20px rgba(16, 185, 129, 0.3);
    animation: scaleIn 0.5s ease;
}

@keyframes scaleIn {
    from {
        transform: scale(0);
    }

    to {
        transform: scale(1);
    }
}

.success-icon .mdi {
    color: #fff;
    font-size: 48px;
}

.success-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: #333;
    margin-bottom: 12px;
}

.success-message {
    font-size: 1rem;
    color: #666;
    margin-bottom: 32px;
    line-height: 1.6;
}

.success-details {
    background: var(--white, #fff);
    border: 2px solid #e0e0e0;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 32px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
}

.detail-row:not(:last-child) {
    border-bottom: 1px solid #e0e0e0;
}

.detail-label {
    font-size: 1rem;
    color: #666;
    font-weight: 500;
}

.detail-value {
    font-size: 1.1rem;
    color: #333;
    font-weight: 600;
}

.success-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
}

.success-actions .btn {
    width: 100%;
    padding: 12px 24px;
    font-size: 1rem;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.success-actions .btn-primary {
    background: var(--color-oo, #d97706);
    border: none;
    color: #fff;
}

.success-actions .btn-primary:hover {
    background: var(--color-oo-400, #b45309);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3);
}

.success-actions .btn-outline-secondary {
    background: transparent;
    border: 2px solid #e0e0e0;
    color: #666;
}

.success-actions .btn-outline-secondary:hover {
    background: #f8f9fa;
    border-color: #999;
    color: #333;
}

/* Common Styles */
.empty-state {
    text-align: center;
    padding: 4rem 2rem;
    color: #999;
}

.empty-state .mdi {
    font-size: 4rem;
    margin-bottom: 1rem;
    display: block;
    opacity: 0.5;
}

.empty-state p {
    margin: 0;
    font-size: 1.1rem;
}

.justify-content-end {
    justify-content: flex-end;
}

.spinner-border {
    display: inline-block;
    width: 1rem;
    height: 1rem;
    vertical-align: text-bottom;
    border: 0.2em solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: spinner-border 0.75s linear infinite;
}

.spinner-border-sm {
    width: 0.875rem;
    height: 0.875rem;
    border-width: 0.15em;
}

.mr-2 {
    margin-right: 0.5rem;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}

@keyframes spinner-border {
    to {
        transform: rotate(360deg);
    }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Responsive Styles */
@media (max-width: 768px) {
    .page-content {
        padding: 1.5rem 1rem;
    }

    .featured-content h5 {
        font-size: 1.05rem;
    }

    .plan-group {
        padding: 16px;
    }

    .boost-plan-option {
        padding: 14px;
    }

    .plan-duration {
        font-size: 0.95rem;
    }

    .plan-price {
        font-size: 1rem;
    }

    .total-price {
        font-size: 1.5rem;
    }

    .boost-footer {
        align-items: stretch;
        padding: 20px;
        right: 0;
        position: fixed;
        z-index: 150;
        bottom: 0;
    }

    .boost-total {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }

    .success-step {
        min-height: 300px;
        padding: 1.5rem;
    }

    .success-icon {
        width: 64px;
        height: 64px;
    }

    .success-icon .mdi {
        font-size: 36px;
    }

    .success-title {
        font-size: 1.5rem;
    }

    .success-actions {
        gap: 10px;
    }
}

@media (max-width: 576px) {
    .page-content {
        padding: 1rem;
    }

    .featured-plan-card {
        padding: 20px;
    }

    .featured-badge {
        width: 40px;
        height: 40px;
    }

    .featured-badge .mdi {
        font-size: 20px;
    }

    .success-title {
        font-size: 1.25rem;
    }

    .success-message {
        font-size: 0.9rem;
    }

    .detail-label,
    .detail-value {
        font-size: 0.9rem;
    }
}
</style>